import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Stethoscope, Inbox } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import StaffFormDialog from "@/components/provider/StaffFormDialog";
import { providerApi } from "@/lib/api/provider";
import { useI18n } from "@/contexts/I18nContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function StaffPage() {
    const { t, lang } = useI18n();
    const [staff, setStaff] = useState(null);
    const [locations, setLocations] = useState([]);
    const [dialog, setDialog] = useState({ open: false, initial: null });
    const [confirmDel, setConfirmDel] = useState(null);

    const refresh = async () => {
        try {
            const [s, l] = await Promise.all([providerApi.listStaff(), providerApi.listLocations()]);
            setStaff(s); setLocations(l);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        }
    };
    useEffect(() => { refresh(); }, []);

    const save = ({ id, payload }) =>
        id ? providerApi.updateStaff(id, payload) : providerApi.createStaff(payload);

    const onDelete = async () => {
        try {
            await providerApi.deleteStaff(confirmDel.id);
            toast.success(t("provider.staff.deleted"));
            setConfirmDel(null);
            refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        }
    };

    return (
        <div className="max-w-5xl space-y-6 fade-up" data-testid="page-provider-staff">
            <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{t("provider.staff.title")}</h1>
                    <p className="mt-2 text-sm text-muted-foreground">{t("provider.staff.subtitle")}</p>
                </div>
                <Button
                    onClick={() => setDialog({ open: true, initial: null })}
                    disabled={locations.length === 0}
                    data-testid="add-staff-button"
                >
                    <Plus className="h-4 w-4 me-1" />
                    {t("provider.staff.add")}
                </Button>
            </header>

            {locations.length === 0 && (
                <div className="rounded-xl border border-warning/40 bg-warning/5 px-4 py-3 text-sm">
                    {t("provider.staff.noLocationsHint")}
                </div>
            )}

            {staff === null && (
                <div className="surface-card p-12 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
            )}
            {staff && staff.length === 0 && (
                <div className="surface-card p-10 text-center" data-testid="staff-empty">
                    <Inbox className="mx-auto h-8 w-8 text-muted-foreground mb-3" />
                    <p className="text-sm font-medium">{t("provider.staff.empty")}</p>
                </div>
            )}
            {staff && staff.length > 0 && (
                <div className="grid sm:grid-cols-2 gap-4">
                    {staff.map((s) => (
                        <div key={s.id} className="surface-card p-5 group" data-testid={`staff-card-${s.id}`}>
                            <div className="flex items-start gap-3">
                                <div className="icon-tile h-12 w-12 shrink-0">
                                    <Stethoscope className="h-6 w-6" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-base font-semibold truncate">
                                        {lang === "ar" ? s.full_name_ar : s.full_name_en}
                                    </h3>
                                    <p className="text-xs text-muted-foreground truncate">
                                        {lang === "ar" ? (s.role_ar || s.role_en) : (s.role_en || s.role_ar)}
                                    </p>
                                    {s.license_number && <p className="text-[11px] text-muted-foreground mt-0.5">Lic. {s.license_number}</p>}
                                    <div className="mt-2 flex flex-wrap gap-1">
                                        {(s.availability || []).map((slot, i) => {
                                            const loc = locations.find((l) => l.id === slot.location_id);
                                            if (!loc) return null;
                                            return (
                                                <Badge key={i} variant="secondary" className="text-[10px]">
                                                    {loc.name_en} · {(slot.days || []).join("/")} · {slot.from_time}–{slot.to_time}
                                                </Badge>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Button size="icon" variant="ghost" onClick={() => setDialog({ open: true, initial: s })} data-testid={`staff-edit-${s.id}`}>
                                        <Pencil className="h-4 w-4" />
                                    </Button>
                                    <Button size="icon" variant="ghost" onClick={() => setConfirmDel(s)} data-testid={`staff-delete-${s.id}`}>
                                        <Trash2 className="h-4 w-4 text-destructive" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <StaffFormDialog
                open={dialog.open}
                onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}
                initial={dialog.initial}
                locations={locations}
                save={save}
                onSaved={() => refresh()}
            />
            <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
                <AlertDialogContent data-testid="staff-delete-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("provider.staff.confirmDeleteTitle")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("provider.staff.confirmDeleteBody", { name: confirmDel?.full_name_en || "" })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                        <AlertDialogAction onClick={onDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="staff-delete-confirm-button">
                            {t("provider.staff.delete")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
